import { request, requestStream, ResponseData } from "./instance";
import duyin from "@/utils/duyin.json";
import Logger from "@/utils/Logger";
import { removeEmoji } from "@/utils/subtitleMessageHandler";
import { speedToApi } from "@/utils/speedUtils";
import * as textToSpeechVolcano from "./textToSpeechVolcano";

/** seriesId=4、15 使用 Volcano TTS（textToSpeechVolcano），其余使用流式 seed-tts（本模块内） */
const USE_NEW_TTS_SERIES_IDS: number[] = [];

/**
 * 专门为 oldtts 创建的兼容格式
 * 检查 frontend 中是否有有效的 words 数据
 */
function createOldttsCompatibleFrontend(
  frontendJson: string | null | undefined,
  durationMs: number
): string | null {
  if (!frontendJson || typeof frontendJson !== "string") return null;

  try {
    const parsed = JSON.parse(frontendJson);
    const words = parsed?.words;

    // oldtts 肯定会返回 words，直接返回原始数据
    return frontendJson;
  } catch (error) {
    Logger.error("[createOldttsCompatibleFrontend] 解析失败", error);
    return frontendJson;
  }
}

/** oldtts 返回的 frontend 中 start_time/end_time 为毫秒，转为秒以与 audio.currentTime 一致 */
function normalizeFrontendTimesToSeconds(frontendJson: string | null | undefined): string | null {
  if (!frontendJson || typeof frontendJson !== "string") return frontendJson ?? null;
  try {
    const parsed = JSON.parse(frontendJson) as {
      words?: Array<{ start_time?: number; end_time?: number; startTime?: number; endTime?: number; word?: string; [k: string]: any }>;
      [k: string]: any;
    };

    // 如果有 words 数组且不为空，直接处理
    const words = parsed?.words;
    if (!Array.isArray(words) || words.length === 0) {
      return frontendJson;
    }

    // 检查是否需要转换（支持驼峰命名和下划线命名）
    const needConvert = words.some(
      (w) =>
        (w.start_time != null && w.start_time > 100) ||
        (w.end_time != null && w.end_time > 100) ||
        (w.startTime != null && w.startTime > 100) ||
        (w.endTime != null && w.endTime > 100)
    );

    if (!needConvert) return frontendJson;

    const normalized = {
      ...parsed,
      words: words.map((w) => ({
        ...w,
        // 转换下划线命名（oldtts）
        start_time: w.start_time != null ? w.start_time / 1000 : w.start_time,
        end_time: w.end_time != null ? w.end_time / 1000 : w.end_time,
        // 转换驼峰命名（seed-tts，以防万一）
        startTime: w.startTime != null ? w.startTime / 1000 : w.startTime,
        endTime: w.endTime != null ? w.endTime / 1000 : w.endTime,
      })),
    };
    return JSON.stringify(normalized);
  } catch (error) {
    Logger.error("[normalizeFrontendTimesToSeconds] 解析失败", error);
    return frontendJson;
  }
}

const streamingHeaders = {
  "Content-Type": "application/json; charset=UTF-8",
  Authorization: "Bearer;uM5bjb3CknbTRgcC8ikq6uRKRkJQTp7A",
  Accept: "application/json", // 流式响应返回JSON格式，不是直接的音频流
  "Cache-Control": "no-cache", // 禁用缓存，确保实时接收流数据
  "X-Api-App-Id": "2326791206",
  "X-Api-Key": "b929a7fe-d414-45fe-9e85-b96e45c74db3",
  "X-Api-Resource-Id": "seed-tts-1.0",
};

// 创建请求数据的模板函数，避免并发请求时数据互相覆盖
const createRequestData = () => ({
  user: {
    uid: "388808087185088",
  },
  req_params: {
    ssml: "",
    speaker: "zh_female_yingyujiaoyu_mars_bigtts",
    audio_params: {
      format: "mp3",
      sample_rate: 24000,
      speech_rate: 0,
      enable_timestamp: true,
    },
  },
  additions: {
    disable_markdown_filter: true,
  },
});

/** 流式 TTS（seed-tts）的 asyncGetAudio，供非 4/15 课程使用 */
async function asyncGetAudioStreaming(
  str: string,
  voiceConfig: any = null,
  language = "en",
  voiceRatio: number = 0
) {
  let msg = removeEmoji(str);
  if (!msg) {
    Logger.info("not valid text, no need to request audio for:", str);
    return {
      error: true,
      msg: "**空字符串，不需求请求语音**",
    };
  }

  const maxRetries = 3;
  let retryCount = 0;
  let lastError: any = null;

  while (retryCount < maxRetries) {
    const audioRes = await asyncGetAudioRequest(
      str,
      language,
      voiceConfig,
      voiceRatio
    );

    if (!audioRes.error) {
      return audioRes;
    }
    lastError = audioRes;
    retryCount++;
    if (retryCount < maxRetries) {
      Logger.info(
        `TTS request failed, retrying (${retryCount}/${maxRetries})...`
      );
      continue;
    }
  }

  return (
    lastError || {
      error: true,
      msg: "**语音生成错误**",
    }
  );
}

/**
 * 统一 TTS 入口：seriesId 为 4 或 15 时使用 Volcano TTS，否则使用流式 seed-tts。
 * @param seriesId 可选，课程系列 ID；不传时走流式 TTS（兼容旧调用）
 */
export async function asyncGetAudio(
  str: string,
  voiceConfig: any = null,
  language = "en",
  voiceRatio: number = 0,
  seriesId?: number
) {
  const useNewTts =
    seriesId !== undefined &&
    seriesId !== null &&
    USE_NEW_TTS_SERIES_IDS.includes(seriesId);

  if (useNewTts) {
    const res = await textToSpeechVolcano.asyncGetAudio(
      str,
      voiceConfig,
      language,
      voiceRatio
    );
    if (res.error || !res.data) {
      return res;
    }
    // 与流式接口兼容：保留 oldtts 返回的 addition；frontend 中时间为毫秒时转为秒以便字幕高亮与 audio.currentTime 一致
    const existingAddition = (res as any).addition || {};
    const rawFrontend =
      existingAddition.frontend ??
      ((res as any).sentence ? JSON.stringify((res as any).sentence) : null);
    const durationMs = existingAddition.duration ? parseFloat(existingAddition.duration) : undefined;

    // oldtts 特殊处理：先创建兼容格式（将空的 words 数组填充为整个时长的单一 word）
    let processedFrontend = rawFrontend;
    if (rawFrontend && durationMs) {
      processedFrontend = createOldttsCompatibleFrontend(rawFrontend, durationMs);
    }

    // 然后标准化时间戳（毫秒转秒）
    const normalizedFrontend = normalizeFrontendTimesToSeconds(processedFrontend);

    return {
      ...res,
      addition: {
        ...existingAddition,
        duration: existingAddition.duration ?? "0",
        frontend: normalizedFrontend,
      },
    };
  }

  return asyncGetAudioStreaming(str, voiceConfig, language, voiceRatio);
}

/**
 * send request to backend service to get the audio for a text
 * @param {*} str
 * @returns
 */
export async function asyncGetAudioRequest(
  str: string,
  language: string,
  voiceConfig: any,
  voiceRatio: number
) {
  let tmp_str = str;
  const duyinJson: { [key: string]: string } = duyin;

  tmp_str = tmp_str
    ?.replace(/\\n/g, "")
    .replace(/\\\\n/g, "")
    .replace(/\\"/g, "");
  // tmp_str = tmp_str?.replace(/'/g, "");
  // tmp_str = tmp_str?.replace(/`/g, ""); //remove it because it will cause error:you're doing great!  ---> youre doing great!
  // tmp_str = tmp_str?.replace(/"/g, "");
  tmp_str = tmp_str?.replace(/\\/g, "");
  // tmp_str = tmp_str?.replace(/"/g, "");
  // tmp_str = tmp_str?.replace(/"/g, "");
  tmp_str = tmp_str?.replace(/</g, "");
  tmp_str = tmp_str?.replace(/>/g, "");
  tmp_str = tmp_str?.replace(/\*/g, "");
  // tmp_str = tmp_str.replaceAll(
  //   /(\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu,
  //   ""
  // );

  tmp_str = tmp_str?.replace(
    /([\uD83C-\uDBFF\uDC00-\uDFFF]+|[\u2694-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26CE\u26CF\u26D1\u26D3\u26E9\u26EA\u26F0-\u26F5\u26F7\u26F8\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299])/g,
    ""
  );

  for (var key in duyin) {
    tmp_str = tmp_str?.replace(key, duyinJson[key]);
  }

  tmp_str = `<speak>${tmp_str}</speak>`;
  // const numberRegex = /[-+]?\d*\.\d+/g;
  //
  // // 使用 replace 方法替换匹配到的数字
  // tmp_str = tmp_str.replace(numberRegex, (match) => {
  //   return `<say-as interpret-as="number">${match}</say-as>`;
  // });

  // 为每个请求创建独立的数据副本，避免并发请求时数据互相覆盖
  const requestData = createRequestData();
  requestData.req_params.ssml = tmp_str;
  requestData.req_params.speaker = voiceConfig?.voice_type;

  // 设置语速参数
  if (voiceRatio !== undefined && voiceRatio !== null) {
    const speedValue = speedToApi(voiceRatio);
    requestData.req_params.audio_params.speech_rate = speedValue;
  }

  // if (voiceConfig && voiceConfig?.emotion)
  //   requestData.audio["emotion"] = voiceConfig?.emotion;

  let lan = language
    ? language
    : voiceConfig && voiceConfig.language
    ? voiceConfig.language
    : "en";

  // if (lan === "en") {
  //   requestData.audio["speed_ratio"] = 0.9;
  // }

  try {
    // 使用流式请求处理逐行JSON响应
    let response = await requestStream(
      "tts",
      {
        method: "post",
        data: requestData,
        headers: streamingHeaders,
        prefixUrl: "/",
      },
      undefined, // onChunk回调，如果需要实时处理每个chunk可以传入
      true // 流式请求不设置超时
    );

    // 流式响应返回格式：{ code, message, data (拼接后的base64), sentence, audioChunks }
    if (response && response.data) {
      let duration = "0";
      let frontend = null;

      if (response.sentence?.words && response.sentence.words.length > 0) {
        // 计算duration：取最后一个word的endTime
        const lastWord =
          response.sentence.words[response.sentence.words.length - 1];
        duration = lastWord.endTime?.toString() || "0";

        // 将sentence信息转换为frontend格式（JSON字符串）
        frontend = JSON.stringify(response.sentence);
      }

      return {
        code: response.code,
        message: response.message,
        data: response.data,
        sentence: response.sentence,
        addition: {
          duration: duration,
          frontend: frontend,
        },
      };
    } else {
      return {
        error: true,
        msg: "**语音生成错误**",
      };
    }
  } catch (error) {
    Logger.error("TTS request error:", error);
    return {
      error: true,
      msg: "**语音生成错误**",
    };
  }
}
